import type {
  LeadStatus,
  LeadType,
  RfqType,
} from "../schemas/validators/enums";

export type {
  LeadStatus,
  LeadType,
  RfqType,
} from "../schemas/validators/enums";
import { literalValues } from "@shared/validation/literal-values";
import type { ProductIntent } from "../products/validators/core";
import {
  QUOTE_STATUS,
  type QuoteStatus,
} from "../schemas/validators/quoteEnums";

export const LEAD_STATUS = {
  NEW: "new",
  CONFIRMED: "confirmed",
  CONTACTED: "contacted",
  QUOTED: "quoted",
  FOLLOW_UP: "follow_up",
  WON: "won",
  LOST: "lost",
} as const satisfies Record<string, LeadStatus>;

export const LEAD_STATUSES = literalValues(LEAD_STATUS);

export const BUYER_REQUEST_STATE = {
  AWAITING_SELLER: "awaiting_seller",
  PICKUP_REQUESTED: "pickup_requested",
  QUOTE_READY: "quote_ready",
  ORDER_PLACED: "order_placed",
  CLOSED: "closed",
} as const;

export const BUYER_REQUEST_STATES = literalValues(BUYER_REQUEST_STATE);

export type BuyerRequestState = (typeof BUYER_REQUEST_STATES)[number];

export const VENDOR_RFQ_STATE = {
  NEEDS_QUOTE: "needs_quote",
  QUOTE_SENT: "quote_sent",
  WON: "won",
  CLOSED: "closed",
} as const;

export const VENDOR_RFQ_STATES = literalValues(VENDOR_RFQ_STATE);

export type VendorRfqState = (typeof VENDOR_RFQ_STATES)[number];

export const VENDOR_LIVE_QUOTE_STATUSES = [
  QUOTE_STATUS.SENT,
  QUOTE_STATUS.VIEWED,
  QUOTE_STATUS.ACCEPTED,
] as const satisfies ReadonlyArray<QuoteStatus>;

const VENDOR_LIVE_QUOTE_STATUS_SET: ReadonlySet<QuoteStatus> = new Set(
  VENDOR_LIVE_QUOTE_STATUSES,
);

export function resolveVendorRfqState(input: {
  leadStatus: LeadStatus;
  vendorQuoteStatuses: ReadonlyArray<QuoteStatus>;
}): VendorRfqState {
  if (input.leadStatus === LEAD_STATUS.WON) return VENDOR_RFQ_STATE.WON;
  if (input.leadStatus === LEAD_STATUS.LOST) return VENDOR_RFQ_STATE.CLOSED;
  if (
    input.vendorQuoteStatuses.some((status) =>
      VENDOR_LIVE_QUOTE_STATUS_SET.has(status),
    )
  ) {
    return VENDOR_RFQ_STATE.QUOTE_SENT;
  }
  return VENDOR_RFQ_STATE.NEEDS_QUOTE;
}

export const LEAD_SELECTABLE_STATUSES = [
  LEAD_STATUS.NEW,
  LEAD_STATUS.CONTACTED,
  LEAD_STATUS.QUOTED,
  LEAD_STATUS.FOLLOW_UP,
  LEAD_STATUS.WON,
  LEAD_STATUS.LOST,
] as const satisfies readonly LeadStatus[];

export type LeadSelectableStatus = (typeof LEAD_SELECTABLE_STATUSES)[number];

export const LEAD_ACTIVE_STATUSES = [
  LEAD_STATUS.NEW,
  LEAD_STATUS.CONFIRMED,
  LEAD_STATUS.CONTACTED,
  LEAD_STATUS.QUOTED,
  LEAD_STATUS.FOLLOW_UP,
] as const;

export const LEAD_TERMINAL_STATUSES: ReadonlySet<LeadStatus> = new Set([
  LEAD_STATUS.WON,
  LEAD_STATUS.LOST,
]);

export const LEAD_CLOSE_REASONS: ReadonlySet<string> = new Set([
  "buyer_closed",
  "buyer_cancelled",
  "stale_90_days",
  "pickup_window_elapsed",
  "competing_claim_accepted",
  "order_cancelled",
]);

export const LEAD_WAITING_STATUSES: readonly LeadStatus[] =
  LEAD_STATUSES.filter((status) => !LEAD_TERMINAL_STATUSES.has(status));

export function displayLeadStatus(status: LeadStatus): LeadSelectableStatus {
  return status === LEAD_STATUS.CONFIRMED ? LEAD_STATUS.NEW : status;
}

export function leadStatusFilterValues(
  status: LeadStatus,
): readonly LeadStatus[] {
  return status === LEAD_STATUS.NEW
    ? [LEAD_STATUS.NEW, LEAD_STATUS.CONFIRMED]
    : [status];
}

export const LEAD_TYPE = {
  BUY: "buy",
  SELL: "sell",
  RECYCLE: "recycle",
  WASTE: "waste",
  INQUIRY: "inquiry",
} as const satisfies Record<string, LeadType>;

export const LEAD_TYPES = literalValues(LEAD_TYPE);

export const LEAD_TYPE_REQUEST_LABEL = {
  [LEAD_TYPE.BUY]: "Quote Request",
  [LEAD_TYPE.SELL]: "Sale Listing",
  [LEAD_TYPE.RECYCLE]: "Recycling Request",
  [LEAD_TYPE.WASTE]: "Disposal Request",
  [LEAD_TYPE.INQUIRY]: "Inquiry",
} as const satisfies Record<LeadType, string>;

export const UNNAMED_PRODUCT_TYPE = "Packaging";

export const LISTING_INTENT_BY_LEAD_TYPE: Partial<Record<LeadType, ProductIntent>> = {
  [LEAD_TYPE.SELL]: "sell",
  [LEAD_TYPE.RECYCLE]: "recycle",
  [LEAD_TYPE.WASTE]: "recycle",
};

export function leadOffersListing(type: LeadType): boolean {
  return LISTING_INTENT_BY_LEAD_TYPE[type] !== undefined;
}

export function leadRequestsPickup(type: LeadType): boolean {
  return LISTING_INTENT_BY_LEAD_TYPE[type] === "recycle";
}

export const LEAD_TYPE_ACTION = {
  [LEAD_TYPE.BUY]: "buy",
  [LEAD_TYPE.SELL]: "sell",
  [LEAD_TYPE.RECYCLE]: "recycle",
  [LEAD_TYPE.WASTE]: "dispose of",
  [LEAD_TYPE.INQUIRY]: "inquire about",
} as const satisfies Record<LeadType, string>;

export function leadTypeTemplateVariables(type: LeadType): {
  leadTypeLabel: string;
  leadTypeAction: string;
} {
  return {
    leadTypeLabel: LEAD_TYPE_REQUEST_LABEL[type],
    leadTypeAction: LEAD_TYPE_ACTION[type],
  };
}

export type LeadMatchAccountType = "vendor" | "customer";

export const LEAD_MATCH_ACCOUNT_TYPE = {
  [LEAD_TYPE.BUY]: "vendor",
  [LEAD_TYPE.SELL]: "customer",
  [LEAD_TYPE.RECYCLE]: "customer",
  [LEAD_TYPE.WASTE]: "customer",
  [LEAD_TYPE.INQUIRY]: null,
} as const satisfies Record<LeadType, LeadMatchAccountType | null>;

export function isLeadMatchCompanyVendor(
  matchSide: LeadMatchAccountType | null,
  companyType: "customer" | "vendor" | "both",
): boolean {
  if (matchSide === "vendor") return true;
  if (matchSide === "customer") return false;
  return companyType === "vendor";
}

export const RFQ_FORM_TYPES = [
  LEAD_TYPE.BUY,
  LEAD_TYPE.SELL,
  LEAD_TYPE.RECYCLE,
] as const satisfies readonly RfqType[];

export const LEAD_AUDIT_DELETE_BATCH_SIZE = 50;

export const CONTACT_LEAD_SCAN_LIMIT = 25;

export const DETAILS_REQUEST_MIN_QUANTITY: Readonly<Record<string, number>> = {
  "ibc-totes": 24,
};
